import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  ChevronLeft,
  ChevronRight,
  Lightbulb,
  Play,
  Sparkles,
  X,
} from "lucide-react";
import {
  STROKE_ANALYSIS_PHASE_LABEL,
  STROKE_KIND_LABEL,
  analyzeStrokeGesture,
  collectAnalyzableStrokes,
  findStrokeNearMs,
} from "../utils/strokeGestureAnalysis";
import "./StrokeAnalysisPanel.css";

function formatClock(ms) {
  const total = Math.max(0, Math.round(Number(ms) || 0));
  const sec = Math.floor(total / 1000);
  const frac = Math.floor((total % 1000) / 10);
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, "0")}.${String(frac).padStart(2, "0")}`;
}

function formatDuration(ms) {
  if (ms == null || !Number.isFinite(Number(ms))) return "—";
  const total = Math.max(0, Math.round(Number(ms)));
  if (total < 1000) return `${total} ms`;
  return `${(total / 1000).toFixed(2)} s`;
}

function formatDeg(v) {
  if (v == null || !Number.isFinite(Number(v))) return "—";
  return `${Math.round(Number(v))}°`;
}

const FILTERS = [
  { id: "all", labelKey: "strokeFilterAll", label: "All" },
  { id: "fh_topspin_left", labelKey: "strokeFilterFhTopspin", label: "FH topspin" },
  { id: "fh_left", labelKey: "strokeFilterFh", label: "Forehand" },
  { id: "bh_topspin_left", labelKey: "strokeFilterBhTopspin", label: "BH topspin" },
  { id: "bh_left", labelKey: "strokeFilterBh", label: "Backhand" },
];

/**
 * Bottom sheet: classified stroke list, filter, phase timeline, and 0.2× play.
 * Prefers persisted analyses from the server when available for this clip.
 */
export function StrokeAnalysisPanel({
  open,
  onClose,
  detections = null,
  poseSamples = null,
  storedAnalyses = null,
  fullClipPoseReady = false,
  currentTimeSec = 0,
  onSeek,
  onPlayStroke,
  t = (k, d) => d || k,
}) {
  const entries = useMemo(
    () => (fullClipPoseReady ? collectAnalyzableStrokes(detections || {}) : []),
    [detections, fullClipPoseReady]
  );
  const storedById = useMemo(() => {
    const map = new Map();
    const strokes = Array.isArray(storedAnalyses?.strokes)
      ? storedAnalyses.strokes
      : [];
    for (const row of strokes) {
      if (!row?.id || !row.analysis) continue;
      map.set(String(row.id), row.analysis);
    }
    return map;
  }, [storedAnalyses]);
  const [activeId, setActiveId] = useState(null);
  const [kindFilter, setKindFilter] = useState("all");

  const availableFilters = useMemo(() => {
    const present = new Set(entries.map((e) => e.kind));
    return FILTERS.filter((f) => f.id === "all" || present.has(f.id));
  }, [entries]);

  const filtered = useMemo(() => {
    if (kindFilter === "all") return entries;
    return entries.filter((e) => e.kind === kindFilter);
  }, [entries, kindFilter]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      onClose?.();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return undefined;
    if (!filtered.some((e) => e.id === activeId)) {
      const tMs = Math.round(Math.max(0, Number(currentTimeSec) || 0) * 1000);
      const near = findStrokeNearMs(filtered, tMs, { maxDistMs: 4000 });
      setActiveId(near?.id || filtered[0]?.id || null);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, filtered, kindFilter]);

  const activeIndex = Math.max(
    0,
    filtered.findIndex((e) => e.id === activeId)
  );
  const activeEntry = filtered[activeIndex] || null;

  const analysis = useMemo(() => {
    if (!activeEntry) return null;
    const stored = storedById.get(String(activeEntry.id));
    if (stored) return stored;
    return analyzeStrokeGesture({
      stroke: activeEntry.stroke,
      kind: activeEntry.kind,
      index: activeEntry.index,
      samples: poseSamples,
    });
  }, [activeEntry, poseSamples, storedById]);

  const fromDb = Boolean(
    activeEntry && storedById.has(String(activeEntry.id))
  );

  if (!open || typeof document === "undefined") return null;

  const seekPhase = (row) => {
    const ms = row?.tStartMs ?? row?.tMs;
    if (ms == null || !onSeek) return;
    onSeek(ms / 1000);
  };

  const selectEntry = (entry) => {
    if (!entry) return;
    setActiveId(entry.id);
    if (entry.tMs != null && onSeek) onSeek(entry.tMs / 1000);
  };

  const goNeighbor = (delta) => {
    if (!filtered.length) return;
    const next = Math.min(
      filtered.length - 1,
      Math.max(0, activeIndex + delta)
    );
    selectEntry(filtered[next]);
  };

  const playActive = () => {
    if (!activeEntry?.stroke || !onPlayStroke) return;
    onPlayStroke(activeEntry.stroke);
  };

  return createPortal(
    <div className="stroke-analysis" role="presentation">
      <button
        type="button"
        className="stroke-analysis__backdrop"
        aria-label={t("close", "Close")}
        onClick={onClose}
      />
      <div
        className="stroke-analysis__sheet"
        role="dialog"
        aria-modal="true"
        aria-label={t("strokeAnalysis", "Stroke analysis")}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="stroke-analysis__head">
          <div>
            <p className="stroke-analysis__eyebrow">
              <Sparkles size={14} strokeWidth={2.2} aria-hidden />
              {t("strokeAnalysis", "Stroke analysis")}
            </p>
            <h2 className="stroke-analysis__title">
              {analysis
                ? `${analysis.kindLabel} #${analysis.index}`
                : t("strokeAnalysisEmpty", "No stroke near playhead")}
            </h2>
            {analysis?.totalDurationMs != null ? (
              <p className="stroke-analysis__meta">
                {t("strokeAnalysisTotal", "Total {{duration}}", {
                  duration: formatDuration(analysis.totalDurationMs),
                })}
                {analysis.tContactMs != null
                  ? ` · ${t("strokeAnalysisContact", "Contact")} ${formatClock(
                      analysis.tContactMs
                    )}`
                  : ""}
                {fromDb
                  ? ` · ${t("strokeAnalysisSaved", "Saved")}`
                  : ""}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            className="stroke-analysis__close"
            onClick={onClose}
            aria-label={t("close", "Close")}
          >
            <X size={18} strokeWidth={2.2} />
          </button>
        </header>

        {!fullClipPoseReady ? (
          <p className="stroke-analysis__empty">
            {t(
              "strokeAnalysisNeedFullPose",
              "Run pose analysis on the full video (clear In/Out), then open stroke analysis to classify and review strokes."
            )}
          </p>
        ) : null}

        {fullClipPoseReady && availableFilters.length > 1 ? (
          <div className="stroke-analysis__filters" role="tablist" aria-label={t("strokeFilter", "Filter strokes")}>
            {availableFilters.map((f) => (
              <button
                key={f.id}
                type="button"
                role="tab"
                aria-selected={kindFilter === f.id}
                className={kindFilter === f.id ? "is-on" : ""}
                onClick={() => setKindFilter(f.id)}
              >
                {t(f.labelKey, f.label)}
                {f.id !== "all"
                  ? ` (${entries.filter((e) => e.kind === f.id).length})`
                  : ` (${entries.length})`}
              </button>
            ))}
          </div>
        ) : null}

        {fullClipPoseReady && filtered.length ? (
          <ul className="stroke-analysis__list" aria-label={t("strokeList", "Strokes")}>
            {filtered.map((entry, i) => {
              const label =
                STROKE_KIND_LABEL[entry.kind] ||
                entry.kind ||
                t("stroke", "Stroke");
              return (
                <li key={entry.id}>
                  <button
                    type="button"
                    className={entry.id === activeId ? "is-on" : ""}
                    onClick={() => selectEntry(entry)}
                  >
                    <strong>
                      {i + 1}. {label}
                    </strong>
                    <span>
                      {entry.tMs != null ? formatClock(entry.tMs) : "—"}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        ) : null}

        {fullClipPoseReady && filtered.length ? (
          <div className="stroke-analysis__nav" role="group">
            <button
              type="button"
              className="stroke-analysis__nav-btn"
              onClick={() => goNeighbor(-1)}
              disabled={activeIndex <= 0}
              aria-label={t("previousStroke", "Previous stroke")}
            >
              <ChevronLeft size={18} strokeWidth={2.2} />
            </button>
            <span className="stroke-analysis__nav-label">
              {activeIndex + 1} / {filtered.length}
            </span>
            <button
              type="button"
              className="stroke-analysis__nav-btn"
              onClick={() => goNeighbor(1)}
              disabled={activeIndex >= filtered.length - 1}
              aria-label={t("nextStroke", "Next stroke")}
            >
              <ChevronRight size={18} strokeWidth={2.2} />
            </button>
            <button
              type="button"
              className="stroke-analysis__play"
              onClick={playActive}
              disabled={!activeEntry || !onPlayStroke}
              title={t("playStrokeSlow", "Play stroke at 0.2×")}
            >
              <Play size={16} strokeWidth={2.4} aria-hidden />
              {t("playStrokeSlowShort", "Play 0.2×")}
            </button>
          </div>
        ) : null}

        {fullClipPoseReady && !analysis ? (
          <p className="stroke-analysis__empty">
            {t(
              "strokeAnalysisNoneFound",
              "No strokes detected yet. Confirm pose finished on the full clip, then reopen."
            )}
          </p>
        ) : null}

        {analysis ? (
          <>
            <div
              className="stroke-analysis__timeline"
              role="list"
              aria-label={t("strokePhases", "Phases")}
            >
              {analysis.phases.map((row) => (
                <button
                  key={row.key}
                  type="button"
                  role="listitem"
                  className={`stroke-analysis__phase-chip stroke-analysis__phase-chip--${row.key}`}
                  style={{
                    flexGrow: Math.max(1, row.pct || 8),
                    flexBasis: 0,
                  }}
                  onClick={() => seekPhase(row)}
                  title={`${row.label} · ${formatDuration(row.durationMs)}`}
                >
                  <span className="stroke-analysis__phase-chip-label">
                    {STROKE_ANALYSIS_PHASE_LABEL[row.key] || row.label}
                  </span>
                  <span className="stroke-analysis__phase-chip-dur">
                    {row.pct != null ? `${row.pct}%` : formatDuration(row.durationMs)}
                  </span>
                </button>
              ))}
            </div>

            <ul className="stroke-analysis__phases">
              {analysis.phases.map((row) => (
                <li key={`row-${row.key}`} className="stroke-analysis__phase-row">
                  <button
                    type="button"
                    className="stroke-analysis__phase-main"
                    onClick={() => seekPhase(row)}
                  >
                    <span
                      className={`stroke-analysis__phase-dot stroke-analysis__phase-dot--${row.key}`}
                      aria-hidden
                    />
                    <span className="stroke-analysis__phase-copy">
                      <strong>{row.label}</strong>
                      <em>
                        {formatDuration(row.durationMs)}
                        {row.tStartMs != null
                          ? ` · ${formatClock(row.tStartMs)}`
                          : ""}
                      </em>
                    </span>
                  </button>
                  <div className="stroke-analysis__angles" aria-label={t("angles", "Angles")}>
                    <span>
                      {t("kneeShort", "Knee")} {formatDeg(row.angles.leftKnee ?? row.angles.rightKnee)}
                    </span>
                    <span>
                      {t("elbowShort", "Elbow")}{" "}
                      {formatDeg(row.angles.leftElbow ?? row.angles.rightElbow)}
                    </span>
                  </div>
                </li>
              ))}
            </ul>

            <section
              className="stroke-analysis__tips"
              aria-label={t("pointsToImprove", "Points to improve")}
            >
              <h3>
                <Lightbulb size={15} strokeWidth={2.2} aria-hidden />
                {t("pointsToImprove", "Points to improve")}
              </h3>
              <ul>
                {analysis.tips.map((tip) => (
                  <li
                    key={tip.id}
                    className={`stroke-analysis__tip stroke-analysis__tip--${tip.severity}`}
                  >
                    <strong>{tip.title}</strong>
                    <p>{tip.detail}</p>
                  </li>
                ))}
              </ul>
            </section>
          </>
        ) : null}
      </div>
    </div>,
    document.body
  );
}

export default StrokeAnalysisPanel;
